import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '@/services/api';
import { getErrorMessage } from '@/services';
import {
  channelKycService,
  customerKycService,
  type ChannelKycDocument,
  type CustomerKycDocument,
} from '@/services/kycDocuments';

/**
 * KYC document queries.
 *
 * Every list below is consumed by a panel that renders loading, error and
 * empty as THREE distinct states — never two. A failed request rendered as
 * "no documents on file" is an authoritative-looking lie on a compliance
 * screen, and it is this codebase's most repeated bug.
 */

// ─── Customer KYC documents ───────────────────────────────────────────────────

export function useCustomerKycDocuments(customerId: string | undefined) {
  return useQuery({
    queryKey: ['customerKycDocuments', customerId],
    queryFn: () => customerKycService.list(customerId!),
    enabled: !!customerId && customerId !== 'undefined',
  });
}

/** Invalidate every view of this contact a KYC change can move: the document
 *  list, the contact record (a confirmation writes PAN/Aadhaar onto it and can
 *  flip kyc_status), and the customer's own `me` payload. */
function invalidateCustomerKyc(queryClient: ReturnType<typeof useQueryClient>, customerId: string) {
  queryClient.invalidateQueries({ queryKey: ['customerKycDocuments', customerId] });
  queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
  queryClient.invalidateQueries({ queryKey: ['customer', 'me'] });
  queryClient.invalidateQueries({ queryKey: ['customer-me'] });
}

export function useUploadCustomerKycDocument(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation<CustomerKycDocument, unknown, { document_type: string; file: File }>({
    mutationFn: (payload) => customerKycService.upload(customerId, payload),
    onSuccess: (document) => {
      invalidateCustomerKyc(queryClient, customerId);
      // Say what actually happened. "Uploaded" alone would let a document whose
      // reading needs checking look finished.
      toast.success(
        document.extraction_status === 'unconfirmed'
          ? 'Uploaded. Check the details we read before saving them.'
          : 'Document uploaded'
      );
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useDeleteCustomerKycDocument(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => customerKycService.remove(customerId, documentId),
    onSuccess: () => {
      invalidateCustomerKyc(queryClient, customerId);
      toast.success('Document removed');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useVerifyCustomerKycDocument(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, remarks, approve }: { documentId: string; remarks: string; approve: boolean }) =>
      approve
        ? customerKycService.verify(customerId, documentId, remarks)
        : customerKycService.reject(customerId, documentId, remarks),
    onSuccess: (_data, variables) => {
      invalidateCustomerKyc(queryClient, customerId);
      toast.success(variables.approve ? 'Document verified' : 'Document rejected');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useConfirmCustomerKycExtraction(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentId,
      fields,
      discard,
    }: {
      documentId: string;
      fields?: Record<string, string>;
      discard?: boolean;
    }) => customerKycService.confirmExtraction(customerId, documentId, { fields, discard }),
    onSuccess: (data, variables) => {
      invalidateCustomerKyc(queryClient, customerId);
      toast.success(
        variables.discard
          ? 'Reading discarded — nothing was saved to the contact.'
          : `Saved ${Object.keys(data.updated_fields).length} field(s) to the contact.`
      );
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

// ─── Channel KYC documents ────────────────────────────────────────────────────

export function useChannelKycDocuments(channelId: string | undefined) {
  return useQuery({
    queryKey: ['channelKycDocuments', channelId],
    queryFn: () => channelKycService.list(channelId!),
    enabled: !!channelId,
  });
}

export function useUploadChannelKycDocument(channelId: string) {
  const queryClient = useQueryClient();

  return useMutation<ChannelKycDocument, unknown, { document_type: string; file: File }>({
    mutationFn: (payload) => channelKycService.upload({ channel: channelId, ...payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channelKycDocuments', channelId] });
      toast.success('Document uploaded');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useDeleteChannelKycDocument(channelId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => channelKycService.remove(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channelKycDocuments', channelId] });
      toast.success('Document removed');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useVerifyChannelKycDocument(channelId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, remarks, approve }: { documentId: string; remarks: string; approve: boolean }) =>
      approve ? channelKycService.verify(documentId, remarks) : channelKycService.reject(documentId, remarks),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['channelKycDocuments', channelId] });
      toast.success(variables.approve ? 'Document verified' : 'Document rejected');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/**
 * Submit an agent's uploaded documents for the broker's review.
 *
 * Lives here rather than beside `useSubmitPOSPDocuments` in `useBrokers.ts`
 * because it is no longer the same call: that one posted a FormData of files to
 * an endpoint that read them for `.size` and threw the bytes away. Files now go
 * one at a time to `/brokers/channel-documents/`, and this action does the only
 * thing left — move the application along, and refuse when nothing is behind
 * it. The response names what is still outstanding.
 */
export function useSubmitChannelDocumentsForReview(pospId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{
        status: string;
        missing_documents: string[];
        message: string;
      }>(`/brokers/posps/${pospId}/submit-documents/`, {});
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['posp', pospId] });
      // Say what actually happened. "Submitted" over an incomplete set is the
      // reassuring-but-wrong message that leaves an agent waiting on a review
      // that will never start.
      if (data.missing_documents.length > 0) toast(data.message);
      else toast.success(data.message);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}
